import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import { SupplyCategory } from '@ebalance/proto/bfftypes/v1/supply_category_pb'
import {
  type ImportSupply48DataRowResult,
  ImportSupply48DataRowStatus,
} from '@ebalance/proto/bffuser/v1/supply_48_data_service_pb'
import { useTranslation } from 'react-i18next'

import { Text } from '@enechain/ecloud-designsystem'

import {
  CsvDiffErrorText,
  CsvDiffTable,
  type CsvDiffTableColumn,
  type CsvDiffTone,
  csvDiffBadgeSpec,
} from '~/components/common/CsvUpload/CsvDiffTable'
import {
  type EnumBadgeSpec,
  EnumStatusBadge,
} from '~/components/common/EnumStatusBadge'
import { convertAreaLabel } from '~/models/area'
import type { ParsedCsvImportRow } from '~/models/supply48DataCsv'
import type { SummaryCardFilter } from '~/pages/Supply48DataCsvUpload/components/SummaryCards'

type Props = {
  rowResults: ImportSupply48DataRowResult[]
  /**
   * client parse 結果 (rowNumber キー引き)。ImportSupply48DataRowResult
   * (response) は取引ID / 日付を持たない (backend が取引ID → leg 解決結果の
   * みを返す設計、D3) ため、入力そのものである取引ID / 日付は送信元の
   * parse 結果から表示する (= ユーザー入力の再表示)。
   */
  parsedRows: ParsedCsvImportRow[]
  /**
   * サマリカードのクリックフィルタ選択値。'all' = 全行表示 (初期値)。
   */
  filter: SummaryCardFilter
}

/** filter に応じて表示対象の行を絞り込む (表示用フィルタ、件数の再計算ではない)。 */
function filterRows(
  rowResults: ImportSupply48DataRowResult[],
  filter: SummaryCardFilter,
): ImportSupply48DataRowResult[] {
  switch (filter) {
    case 'all':
      return rowResults
    case 'unchanged':
      return rowResults.filter(
        (r) => r.status === ImportSupply48DataRowStatus.UNCHANGED,
      )
    case 'new':
      return rowResults.filter(
        (r) => r.status === ImportSupply48DataRowStatus.NEW,
      )
    case 'changed':
      return rowResults.filter(
        (r) => r.status === ImportSupply48DataRowStatus.CHANGED,
      )
    case 'deleted':
      return rowResults.filter(
        (r) => r.status === ImportSupply48DataRowStatus.DELETED,
      )
    case 'diff':
      // AC-DUC-012 / D11: 差分のみ = 新規 + 変更 + 削除。
      return rowResults.filter(
        (r) =>
          r.status === ImportSupply48DataRowStatus.NEW ||
          r.status === ImportSupply48DataRowStatus.CHANGED ||
          r.status === ImportSupply48DataRowStatus.DELETED,
      )
    case 'error':
      return rowResults.filter(
        (r) => r.status === ImportSupply48DataRowStatus.ERROR,
      )
    case 'warning':
      // AC-S4D-045: status でなく warningMessages の有無で絞り込む
      // (時間帯区分整合の警告は NEW/CHANGED/UNCHANGED いずれの status でも
      // 発生しうる、SummaryCards の warningRowCount と同じ判定基準)。
      return rowResults.filter((r) => r.warningMessages.length > 0)
  }
}

/** buySell の表示ラベル (D3: サーバー導出値をそのまま表示、鉄則1)。 */
function buySellLabel(b: Side, t: (key: string) => string): string {
  switch (b) {
    case Side.BUY:
      return t('page:supply48DataCsvUpload.step2.buySell.buy')
    case Side.SELL:
      return t('page:supply48DataCsvUpload.step2.buySell.sell')
    default:
      return t('page:supply48DataCsvUpload.step2.buySell.unspecified')
  }
}

function categoryLabel(c: SupplyCategory, t: (key: string) => string): string {
  switch (c) {
    case SupplyCategory.BILATERAL:
      return t('page:supply48DataCsvUpload.step2.category.bilateral')
    case SupplyCategory.MARKET:
      return t('page:supply48DataCsvUpload.step2.category.market')
    case SupplyCategory.BACKUP_REGULAR:
      return t('page:supply48DataCsvUpload.step2.category.backupRegular')
    case SupplyCategory.FUTURES:
      return t('page:supply48DataCsvUpload.step2.category.futures')
    default:
      return t('page:supply48DataCsvUpload.step2.category.unspecified')
  }
}

function areaLabel(a: AreaCode): string {
  return convertAreaLabel(a)
}

/**
 * 取込行ステータス → 差分表の行の種類 (行の地色・バッジの色を決める)。
 *
 * DELETED は design (`bilateral-trade.html` 等の削除行) どおり中立の地 + 赤のバッジ。
 * 以前は CHANGED と同じ warning (黄) で、 同じ表の中で色では区別できなかった。
 */
const ROW_STATUS_TONE: Record<
  ImportSupply48DataRowStatus,
  CsvDiffTone | undefined
> = {
  [ImportSupply48DataRowStatus.UNSPECIFIED]: undefined,
  [ImportSupply48DataRowStatus.CHANGED]: 'changed',
  [ImportSupply48DataRowStatus.UNCHANGED]: 'unchanged',
  [ImportSupply48DataRowStatus.ERROR]: 'error',
  [ImportSupply48DataRowStatus.NEW]: 'new',
  [ImportSupply48DataRowStatus.DELETED]: 'deleted',
}

/** 取込行ステータス → 状態バッジ表示仕様 (色は `ROW_STATUS_TONE` と同じ写像から引く)。 */
const ROW_STATUS_BADGE: Record<ImportSupply48DataRowStatus, EnumBadgeSpec> = {
  [ImportSupply48DataRowStatus.UNSPECIFIED]: {
    status: 'neutral',
    labelKey: 'page:supply48DataCsvUpload.step2.status.unspecified',
  },
  [ImportSupply48DataRowStatus.CHANGED]: csvDiffBadgeSpec(
    'changed',
    'page:supply48DataCsvUpload.step2.status.changed',
  ),
  [ImportSupply48DataRowStatus.UNCHANGED]: csvDiffBadgeSpec(
    'unchanged',
    'page:supply48DataCsvUpload.step2.status.unchanged',
  ),
  [ImportSupply48DataRowStatus.ERROR]: csvDiffBadgeSpec(
    'error',
    'page:supply48DataCsvUpload.step2.status.error',
  ),
  [ImportSupply48DataRowStatus.NEW]: csvDiffBadgeSpec(
    'new',
    'page:supply48DataCsvUpload.step2.status.new',
  ),
  [ImportSupply48DataRowStatus.DELETED]: csvDiffBadgeSpec(
    'deleted',
    'page:supply48DataCsvUpload.step2.status.deleted',
  ),
}

/** 差分表の列数 (読み込み中の仮の行を同じ列数で並べるため)。 */
export const RESULT_TABLE_COLUMN_COUNT = 11

/** ERROR 行は leg 解決に失敗していて表示すべき値が無い。 */
function isErrorRow(r: ImportSupply48DataRowResult): boolean {
  return r.status === ImportSupply48DataRowStatus.ERROR
}

/**
 * 差分表の列定義。 コンポーネント本体の外に置き、 cell が render のたびに
 * 新しいコンポーネント型にならないようにする (react/no-unstable-nested-components)。
 *
 * error を他の判定に優先させているのは防御的な措置: backend は ERROR 行を
 * resolveImportRowDiff の早期 return で確定するため error_messages と
 * warning_messages が同時に非空になる行は現行実装では生成されない。 それでも
 * wire 契約上は両方非空という組み合わせを排除できないため、 表示側は error を
 * 優先する側に倒しておく。
 */
function buildColumns(
  t: (key: string) => string,
  parsedByRowNumber: Map<number, ParsedCsvImportRow>,
): Array<CsvDiffTableColumn<ImportSupply48DataRowResult>> {
  return [
    {
      key: 'status',
      header: t('page:supply48DataCsvUpload.step2.columns.status'),
      cell: (r) => <EnumStatusBadge map={ROW_STATUS_BADGE} value={r.status} />,
    },
    {
      key: 'rowNumber',
      header: t('page:supply48DataCsvUpload.step2.columns.rowNumber'),
      align: 'right',
      cell: (r) => r.rowNumber,
    },
    {
      key: 'publicTradeId',
      header: t('page:supply48DataCsvUpload.step2.columns.publicTradeId'),
      mono: true,
      cell: (r) => parsedByRowNumber.get(r.rowNumber)?.publicTradeId ?? '-',
    },
    {
      key: 'date',
      header: t('page:supply48DataCsvUpload.step2.columns.date'),
      cell: (r) => parsedByRowNumber.get(r.rowNumber)?.deliveryDate ?? '-',
    },
    {
      key: 'buySell',
      header: t('page:supply48DataCsvUpload.step2.columns.buySell'),
      align: 'center',
      cell: (r) => (isErrorRow(r) ? '-' : buySellLabel(r.buySell, t)),
    },
    {
      key: 'category',
      header: t('page:supply48DataCsvUpload.step2.columns.category'),
      cell: (r) => (isErrorRow(r) ? '-' : categoryLabel(r.category, t)),
    },
    {
      key: 'area',
      header: t('page:supply48DataCsvUpload.step2.columns.area'),
      cell: (r) => (isErrorRow(r) ? '-' : areaLabel(r.areaCode)),
    },
    {
      key: 'tradingPartnerName',
      header: t('page:supply48DataCsvUpload.step2.columns.tradingPartnerName'),
      cell: (r) => (isErrorRow(r) ? '-' : r.tradingPartnerName),
    },
    // DELETED 行も件数を出す (D11')。AC-SUPPLY-029 が削除を
    // 「48 コマを空にした変更」と定義したので、空にするコマ数を
    // 変更コマ数として数える。AC-SUPPLY-027 で行の値が消える以上、
    // 削除規模が見えないまま確定できる状態にはしない —
    // 48点データは誤削除防止 (AC-SUPPLY-019〜022) を欠番にしており、
    // 差分プレビューが唯一の確認手段。'-' 潰しは ERROR 行だけ
    // (leg 解決に失敗していて表示すべき値が無い)。
    {
      key: 'changedKomaCount',
      header: t('page:supply48DataCsvUpload.step2.columns.changedKomaCount'),
      align: 'right',
      cell: (r) => (isErrorRow(r) ? '-' : r.changedKomaCount),
    },
    {
      key: 'skippedKomaCount',
      header: t('page:supply48DataCsvUpload.step2.columns.skippedKomaCount'),
      align: 'right',
      cell: (r) => (isErrorRow(r) ? '-' : r.skippedKomaCount),
    },
    {
      key: 'note',
      header: t('page:supply48DataCsvUpload.step2.columns.note'),
      cell: (r) => {
        if (isErrorRow(r)) {
          return (
            <CsvDiffErrorText>{r.errorMessages.join(', ')}</CsvDiffErrorText>
          )
        }
        // AC-S4D-045: 時間帯区分整合の警告は行エラーではない
        // (status は実際の diff 結果どおり NEW/CHANGED/UNCHANGED のまま)。
        // 行は正常に反映されるので、 エラー行と同じ '-' 潰しはしない。
        if (r.warningMessages.length > 0) {
          return (
            <Text as="span" color="Text.warningMid">
              {r.warningMessages.join(', ')}
            </Text>
          )
        }
        return ''
      },
    },
  ]
}

/**
 * Step2 差分プレビューの行別結果テーブル (design `admin/uploads/supply-48data.html`
 * `#diff-wrap`)。 行の地色・ステータスバッジ・見出しの見た目は CSV アップロード共通の
 * `CsvDiffTable` が持つ。
 *
 * [design差異] design の列は「ステータス / 行番号 / 取引ID / 受渡日 / 指定コマ /
 * 変更コマ / 備考」。 本画面は ImportSupply48Data の行結果が返す値 (leg 解決した
 * 売買・カテゴリ・エリア・相対先、 変更コマ数・反映対象外コマ数) を並べる。
 * 指定コマ数は RPC が返さないため出していない。
 */
export const RowResultTable = ({
  rowResults,
  parsedRows,
  filter,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const parsedByRowNumber = new Map(parsedRows.map((r) => [r.rowNumber, r]))

  return (
    <CsvDiffTable
      columns={buildColumns(t, parsedByRowNumber)}
      getRowKey={(r) => String(r.rowNumber)}
      getRowTone={(r) => ROW_STATUS_TONE[r.status]}
      rows={filterRows(rowResults, filter)}
    />
  )
}
